<template>
  <DrawerModal
    class="RunModalView"
    :aria-busy="!isRunLoaded"
    :aria-label="label ? `Run details for ${label}` : undefined"
    :label="isRunLoaded && !isHeroVisible && !isTransitioning ? label : undefined"
  >
    <template #nav>
      <RunNotificationToggleView
        v-if="isRunLoaded"
        class="RunModalView__notificationToggle"
        v-bind="{runId}"
      />
    </template>
    <RunView
      v-if="run"
      :runSummary="run"
      v-bind="{ fullRun }"
      :isReady="isRunLoaded"
      @update:isHeroVisible="isHeroVisible = $event"
      @delete="closeActiveModal()"
    />
  </DrawerModal>
</template>

<script lang="ts" setup>
import DrawerModal from "@/components/DrawerModal.vue";
import useRunSchedule from "@/composables/useRunSchedule";
import useModalStore from "@/stores/useModalStore";
import useRunCarouselStore, { RunForCarousel } from "@/stores/useRunCarouselStore";
import useRunStore from "@/stores/useRunStore";
import RunView from "@/views/RunView.vue";
import { format } from "date-fns";
import { storeToRefs } from "pinia";
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import RunNotificationToggleView from "../RunNotificationToggleView.vue";

const {
  runId,
  isTransitioning = false,
} = defineProps<{
  runId: string,
  isTransitioning?: boolean,
}>();

const {
  closeActiveModal,
} = useModalStore();

const {
  getRun,
  fetchRun,
} = useRunStore();

const {
  currentRuns,
} = storeToRefs(useRunCarouselStore());

const isHeroVisible = ref(true);

const existingRun = computed((): RunForCarousel | null => {
  return currentRuns.value.find((run) => run.id === runId) || null;
});

const fullRun = computed(() => getRun(runId));
const run = computed(() => fullRun.value || existingRun.value);

const isRunLoaded = computed(() => !!fullRun.value);

const {
  startDateInTimezone,
  formattedShortDate,
  formattedTimeRange,
} = useRunSchedule(run);

const label = computed(() => {
  if (!startDateInTimezone.value) return;

  return `${format(startDateInTimezone.value, "EEE")}, ${formattedShortDate.value} @ ${formattedTimeRange.value}`;
});

watch(() => runId, async () => {
  try {
    await fetchRun(runId);

    if (!getRun(runId)) {
      alert("Run not found");
      closeActiveModal();
    }
  } catch (err) {
    closeActiveModal();
    alert((err as Error).message);
  }
}, {
  immediate: true,
});

onMounted(() => {
  document.addEventListener("visibilitychange", handleVisibilityChange);
});

onBeforeUnmount(() => {
  document.removeEventListener("visibilitychange", handleVisibilityChange);
});

function handleVisibilityChange () {
  if (document.visibilityState === "visible") fetchRun(runId);
}
</script>

<style lang="scss" scoped>
.RunModalView__notificationToggle {
  position: relative;
  top: 1px;
  left: -6px;
}
</style>
